import { CheckCircle2, PartyPopper, PlusCircle, RotateCcw, XCircle } from "lucide-react";
import { useEffect, useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import useTranslations from "@/shared/lang/useTranslations";

import { usePayinLiveStatus } from "../hooks/usePayinLiveStatus";
import type { InitiatePayinResponse } from "../services/paymentsPayin.service";
import { payinOutcome } from "../utils/payinStatusProgress";
import { pixQrDataUrl } from "../utils/pixQrDataUrl";
import { PayinQrCard } from "./PayinQrCard";

/** Backend money fields are decimal strings ("1000.00000000") — parse before formatting (COP has no cents). */
function formatCOP(value: string | null | undefined): string {
  return Math.round(Number(value ?? 0)).toLocaleString("es-CO");
}

export interface PayinBrebSuccessScreenProps {
  result: InitiatePayinResponse;
  onNewPayin: () => void;
}

/**
 * Success screen for a Colombia / Bre-B runtime payin (`POST /internal/payments/payins`). Renders the dynamic
 * Bre-B QR from the EMV payload (client-side) and the fee/net breakdown, and stays live while open:
 * `usePayinLiveStatus` (socket `payin:updated` + polling fallback) flips the screen to "paid" the moment the
 * provider webhook settles the payin. Self-contained on purpose — it never touches the legacy PHP
 * `PayinSuccessScreen`.
 */
export function PayinBrebSuccessScreen({ result, onNewPayin }: PayinBrebSuccessScreenProps) {
  const { t, activeLanguage } = useTranslations("payins");
  const [qrSrc, setQrSrc] = useState<string | null>(null);
  const liveStatus = usePayinLiveStatus(result.id, { expiresAt: result.expires_at });
  // Every final status hides the QR, REVERSED included: offering it again would invite a second payment.
  const outcome = payinOutcome(liveStatus);

  useEffect(() => {
    if (!result.qr_code_payload) return;
    let cancelled = false;
    void pixQrDataUrl(result.qr_code_payload).then((url) => {
      if (!cancelled) setQrSrc(url);
    });
    return () => {
      cancelled = true;
    };
  }, [result.qr_code_payload]);

  const totalFee = Number(result.fee_total);
  const expiresLabel = result.expires_at
    ? new Date(result.expires_at).toLocaleString(activeLanguage, { dateStyle: "medium", timeStyle: "short" })
    : null;

  return (
    <div className="mx-auto max-w-md space-y-4">
      {/* Live outcome: the webhook settled (or rejected) the payin while this screen was open. */}
      {outcome === "paid" && (
        <ColocaAlert
          variant="success"
          icon={PartyPopper}
          heading={t("breb_paid_title")}
          text={t("breb_paid_text")}
        />
      )}
      {outcome === "refused" && (
        <ColocaAlert variant="destructive" icon={XCircle} text={t("breb_rejected_text")} />
      )}
      {outcome === "reversed" && (
        <ColocaAlert
          variant="warning"
          icon={RotateCcw}
          heading={t("payin_reversed_title")}
          text={t("payin_reversed_text")}
        />
      )}

      <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
        {/* Header */}
        <div className="flex items-start gap-3 border-b border-border bg-primary/5 p-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary">
            <CheckCircle2 className="h-5 w-5 text-primary-foreground" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-primary">{t("breb_success_title")}</p>
            <p className="mt-0.5 font-mono text-xs text-muted-foreground">
              {t("breb_success_id")} · {result.id}
            </p>
            <p className="mt-1.5 text-sm text-foreground">
              {t("breb_success_amount")}{" "}
              <strong className="font-mono">{formatCOP(result.gross_amount)} COP</strong>
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {t("breb_success_fee")}:{" "}
              <span className="font-mono font-medium text-foreground">
                {totalFee === 0 ? t("breb_success_fee_free") : `${formatCOP(result.fee_total)} COP`}
              </span>
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {t("breb_success_net")}:{" "}
              <span className="font-mono font-medium text-foreground">
                {formatCOP(result.net_amount)} COP
              </span>
            </p>
          </div>
        </div>

        <div className="space-y-4 p-4">
          {/* No key on a QR with an amount: a typed transfer becomes another collection and this one expires. */}
          {result.qr_code_payload && outcome === "open" && (
            <PayinQrCard
              src={qrSrc}
              rail="BREB"
              footer={expiresLabel ? `${t("breb_success_expires")} ${expiresLabel}` : undefined}
              amount={`${formatCOP(result.gross_amount)} COP`}
            />
          )}
        </div>
      </div>

      <ColocaButton variant="primary" icon={PlusCircle} className="w-full" onClick={onNewPayin}>
        {t("breb_success_new")}
      </ColocaButton>
    </div>
  );
}
